import { useEffect, useRef, useState } from 'react';
import { Lock, Minus, Plus, X } from 'lucide-react';
import BottomSheet from '../../../components/ui/BottomSheet';
import { fmtPrice, cn } from '../../../lib/utils';
import EditableCell from './EditableCell';
import { unitShort, RISK_UNIT_OPTIONS } from './riskUnits';
import { qtyColumnLabel, qtyRowField } from './qtyModes';
import { isRowLocked, ORDER_TYPE_OPTIONS, RISK_REFERENCE_OPTIONS, ChangePercentBadge } from './WatchlistTable';

// Short risk label ("Target %"/"SL %"/"Target pts"/"Target Price") built from the
// same unitShort() primitive riskColumnLabel() uses internally — just without parens.
function shortRiskLabel(base, unit) {
  const suffix = unitShort(unit);
  return suffix === 'Price' ? `${base} Price` : `${base} ${suffix}`;
}

// Blank = only null/undefined/empty-string. Numeric 0 and string "0" are valid
// user values and must NOT be treated as blank (so user-entered 0 is preserved).
function isBlankValue(v) {
  return v == null || (typeof v === 'string' && v.trim() === '');
}

// Consistent sizing for EditableCell inputs inside this sheet. A fixed h-10 keeps
// every control (inputs AND the adjacent selects) exactly the same height so the
// Risk Unit / Target / SL and Order Type / MPP rows line up. EditableCell renders a
// bare <input>, so this Tailwind arbitrary child-selector targets it directly
// without needing any change to EditableCell.jsx.
const INPUT_SIZE_WRAP = '[&>input]:px-2 [&>input]:h-10 [&>input]:text-base [&>input]:tabular-nums';
// Selects styled to match the h-10 input footprint above.
const SELECT_CLS = 'w-full h-10 bg-bg-surface border border-bg-border rounded px-2 text-xs font-mono text-txt-primary focus:outline-none focus:border-amber/60';

function normSymbol(value) {
  return String(value || '').trim().toUpperCase();
}

function hasValidPrice(value) {
  const n = value == null || value === '' ? NaN : Number(value);
  return Number.isFinite(n) && n > 0;
}

function findOpenPositionForRow(row, openPositions) {
  if (!row) return null;
  if (row.backendId != null) {
    return openPositions.find((p) => p?.exitRowId === row.backendId) || null;
  }
  const rowSymbol = normSymbol(row.symbol);
  if (!rowSymbol) return null;
  return openPositions.find((p) => (
    [p?.sourceSymbol, p?.symbol, p?.positionSymbol, p?.resolvedOptionSymbol]
      .some((symbol) => normSymbol(symbol) === rowSymbol)
  )) || null;
}

function activeTradeForRow(row, openPositions) {
  const pos = findOpenPositionForRow(row, openPositions);
  const netQty = Number(pos?.netQty) || 0;
  if (!pos || netQty === 0) return null;

  const side = netQty > 0 ? 'BUY' : 'SELL';
  const avgPrice = side === 'BUY' ? pos.buyAvg : pos.sellAvg;
  const price = hasValidPrice(avgPrice) ? avgPrice : row.lastTriggerPrice;
  return { side, price: hasValidPrice(price) ? price : null };
}

function ActiveTradePill({ trade }) {
  if (!trade) return null;
  const isBuy = trade.side === 'BUY';
  return (
    <span
      className={cn(
        'ml-2 inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0 text-2xs font-mono font-semibold',
        isBuy ? 'bg-bull/10 border-bull/30 text-bull' : 'bg-bear/10 border-bear/30 text-bear',
      )}
    >
      <span>{trade.side}</span>
      {trade.price != null && (
        <>
          <span className="opacity-70">@</span>
          <span className="truncate">{fmtPrice(trade.price)}</span>
        </>
      )}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-2xs font-medium text-txt-label">{label}</div>
      {children}
    </div>
  );
}

const MODE_BANNER = {
  PAPER: { title: 'PAPER MODE', subtext: 'No real broker order will be placed', cls: 'bg-info/10 border-info/30 text-info' },
  LIVE: { title: 'LIVE MODE', subtext: 'Real broker order can be placed', cls: 'bg-bear/10 border-bear/30 text-bear' },
  UNKNOWN: { title: 'MODE UNKNOWN', subtext: 'Please verify strategy mode before trading', cls: 'bg-bg-hover border-bg-border text-txt-muted' },
};

// Compact inline mode pill — same safety cue as before (● PAPER MODE · No real
// broker order will be placed) but a single short line instead of a tall banner.
function ModePill({ mode }) {
  const meta = MODE_BANNER[mode] || MODE_BANNER.UNKNOWN;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 max-w-full min-w-0', meta.cls)}>
      <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
      <span className="text-2xs font-semibold whitespace-nowrap">{meta.title}</span>
      <span className="text-2xs opacity-80 truncate">· {meta.subtext}</span>
    </span>
  );
}

function OrderEntrySheet({ strategy, row, riskUnit, onBuy, onSell, onEditCell, onDelete, onRowRiskUnitChange, onClose }) {
  const qtyMode = strategy.manual_trading?.qty_mode;
  const qtyKey = qtyRowField(qtyMode);
  const locked = isRowLocked(strategy.status, row.status);
  const showRiskReference = String(strategy.manual_trading?.trade_in || '').toUpperCase() === 'OPTION';

  // FIXED_QTY-only stepper arithmetic — empty/non-numeric current value behaves as 0,
  // so "+" from empty becomes 1; "−" is disabled at/below 1 (backend requires a positive
  // whole number for FIXED_QTY). Both call the exact same onEditCell path EditableCell uses.
  const qty = Number(row[qtyKey]) || 0;
  const decQty = () => { if (qty > 1) onEditCell(row, qtyKey, String(qty - 1)); };
  const incQty = () => onEditCell(row, qtyKey, String(qty + 1));

  // One-shot defaults on sheet open: in Fixed Qty mode, Qty → 1 when blank; MPP → 1
  // when blank. Runs only on mount (ref guard) so user edits are never reset. Numeric
  // 0 / "0" are NOT blank (see isBlankValue), so a user-entered 0 is preserved. Existing
  // configured/saved values are also preserved (non-blank). No lot-size logic.
  const defaultsInitRef = useRef(false);
  useEffect(() => {
    if (defaultsInitRef.current) return;
    defaultsInitRef.current = true;
    if (qtyMode !== 'BY_MARGIN' && isBlankValue(row[qtyKey])) {
      onEditCell(row, qtyKey, '1');
    }
    if (isBlankValue(row.mpp)) {
      onEditCell(row, 'mpp', '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BottomSheet open onClose={onClose} title={<ModePill mode={strategy.status} />}>
      <div className="space-y-2.5">
        {/* Symbol / LTP — compact single-block header (no Ready badge, no Last Signal). */}
        <div className="rounded-lg bg-bg-surface/40 px-2.5 py-2">
          <span className="block text-lg font-bold text-txt-primary truncate leading-tight">{row.displaySymbol || row.symbol}</span>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="text-2xs font-mono uppercase tracking-widest text-txt-muted">LTP</span>
            <span className="text-xl font-bold text-txt-primary tabular-nums">{fmtPrice(row.quote?.ltp ?? row.ltp)}</span>
            <ChangePercentBadge quote={row.quote} className="text-xs" />
          </div>
        </div>

        {/* Compact form — no section headings; fields read as one dense group. */}
        {/* Qty — compact inline row: label on the left, minus/input/plus on the right.
            BY_MARGIN uses a single value input; FIXED_QTY uses a narrow centered stepper
            input flanked by −/+. Stepper behavior / validation / state are unchanged. */}
        {qtyMode === 'BY_MARGIN' ? (
          <div className="flex items-center gap-2">
            <span className="text-2xs font-medium text-txt-label shrink-0">{qtyColumnLabel(qtyMode, strategy.manual_trading?.segment)}</span>
            <div className={cn(INPUT_SIZE_WRAP, 'w-28')}>
              <EditableCell
                value={row[qtyKey]}
                inputMode="decimal"
                width="100%"
                placeholder=""
                onCommit={(val) => onEditCell(row, qtyKey, val)}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-2xs font-medium text-txt-label shrink-0">{qtyColumnLabel(qtyMode, strategy.manual_trading?.segment)}</span>
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                type="button"
                disabled={qty <= 1}
                onClick={decQty}
                aria-label="Decrease quantity"
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md border border-bg-border text-txt-secondary disabled:opacity-30 disabled:cursor-not-allowed hover:bg-bg-hover transition-colors"
              >
                <Minus size={14} />
              </button>
              <div className={cn(INPUT_SIZE_WRAP, 'w-16 [&>input]:text-center')}>
                <EditableCell
                  value={row[qtyKey]}
                  inputMode="decimal"
                  width="100%"
                  placeholder=""
                  onCommit={(val) => onEditCell(row, qtyKey, val)}
                />
              </div>
              <button
                type="button"
                onClick={incQty}
                aria-label="Increase quantity"
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md border border-bg-border text-txt-secondary hover:bg-bg-hover transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Risk Unit + Target + SL on one compact row — matched control heights. */}
        <div className={cn('grid gap-2', showRiskReference ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
          {showRiskReference && (
            <Field label="Risk">
              <select
                value={String(row.riskReferenceMode || 'OPTION').toUpperCase() === 'BASE' ? 'BASE' : 'OPTION'}
                onChange={(e) => onEditCell(row, 'riskReferenceMode', e.target.value)}
                className={SELECT_CLS}
              >
                {RISK_REFERENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Risk Unit">
            <select
              value={row.riskUnit}
              onChange={(e) => onRowRiskUnitChange(row, e.target.value)}
              className={SELECT_CLS}
            >
              {RISK_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label={shortRiskLabel('Target', row.riskUnit)}>
            <div className={INPUT_SIZE_WRAP}>
              <EditableCell value={row.target} inputMode="decimal" width="100%" placeholder="0" onCommit={(val) => onEditCell(row, 'target', val)} />
            </div>
          </Field>
          <Field label={shortRiskLabel('SL', row.riskUnit)}>
            <div className={INPUT_SIZE_WRAP}>
              <EditableCell value={row.stopLoss} inputMode="decimal" width="100%" placeholder="0" onCommit={(val) => onEditCell(row, 'stopLoss', val)} />
            </div>
          </Field>
        </div>

        {/* Order Type + MPP on one compact row — matched control heights. */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="Order Type">
            <select
              value={String(row.orderType).toUpperCase() === 'SL-L' ? 'SL-L' : 'Market'}
              onChange={(e) => onEditCell(row, 'orderType', e.target.value)}
              className={SELECT_CLS}
            >
              {ORDER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="MPP">
            <div className={INPUT_SIZE_WRAP}>
              <EditableCell value={row.mpp} inputMode="decimal" width="100%" placeholder="0" onCommit={(val) => onEditCell(row, 'mpp', val)} />
            </div>
          </Field>
        </div>
      </div>

      {/* BUY / SELL — sticky within BottomSheet's own existing scrollable body (not
          position:fixed) so it stays reachable without scrolling through the whole form.
          It's the last child here, so sticky can never cover a field above it. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 mt-2.5 bg-bg-card border-t border-bg-border flex gap-3">
        <button
          type="button"
          disabled={locked}
          onClick={() => { onBuy(strategy, row); onClose(); }}
          className={cn(
            'mt-action flex-1 min-h-[48px] rounded-md text-base font-bold border transition-colors',
            locked
              ? 'bg-bg-hover/40 border-bg-border text-txt-muted cursor-not-allowed opacity-50'
              : 'bg-bull border-bull text-white hover:bg-bull/90',
          )}
        >
          BUY
        </button>
        <button
          type="button"
          disabled={locked}
          onClick={() => { onSell(strategy, row); onClose(); }}
          className={cn(
            'mt-action flex-1 min-h-[48px] rounded-md text-base font-bold border transition-colors',
            locked
              ? 'bg-bg-hover/40 border-bg-border text-txt-muted cursor-not-allowed opacity-50'
              : 'bg-bear border-bear text-white hover:bg-bear/90',
          )}
        >
          SELL
        </button>
      </div>
    </BottomSheet>
  );
}

export default function WatchlistMobileRows({
  strategy, rows, riskUnit, openPositions = [], onBuy, onSell, onEditCell, onDelete, onRowRiskUnitChange, pendingAddedSymbol, onPendingSymbolHandled,
}) {
  // Track identity (not a row snapshot) and look the row up fresh from `rows` on every
  // render — otherwise a stale snapshot would make repeated stepper taps (or any edit)
  // compute from the value at the moment the sheet opened instead of the latest commit.
  const [activeKey, setActiveKey] = useState(null);
  const activeRow = activeKey != null ? rows.find((r) => (r.backendId ?? r.symbol) === activeKey) ?? null : null;

  // M8C: scroll the just-added row into view once it's actually present in `rows` (i.e. after the
  // watchlist refetch lands it — never fires early). Ref is only attached to the matching row below.
  const pendingRowRef = useRef(null);
  useEffect(() => {
    if (!pendingAddedSymbol || !pendingRowRef.current) return;
    pendingRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => onPendingSymbolHandled?.(), 1500);
    return () => clearTimeout(t);
  }, [pendingAddedSymbol, rows, onPendingSymbolHandled]);

  if (rows.length === 0) {
    return <div className="text-center py-6 text-xs text-txt-muted">No symbols in this watchlist</div>;
  }

  return (
    <div className="divide-y divide-bg-border">
      {rows.map((row) => {
        const locked = isRowLocked(strategy.status, row.status);
        const symbol = row.displaySymbol || row.symbol;
        const key = row.backendId ?? symbol;
        const isPending = !!pendingAddedSymbol && String(row.symbol || '').trim().toUpperCase() === pendingAddedSymbol;
        const activeTrade = activeTradeForRow(row, openPositions);
        return (
          <div
            key={key}
            ref={isPending ? pendingRowRef : undefined}
            role="button"
            tabIndex={0}
            onClick={() => setActiveKey(key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveKey(key); } }}
            aria-label={locked ? `${symbol} — locked, view details` : `${symbol} — view details`}
            className={cn(
              'w-full min-h-[44px] flex items-center gap-2 py-1.5 pr-1 text-left transition-colors hover:bg-bg-hover/40 active:bg-bg-hover/60 cursor-pointer',
              locked && 'opacity-60',
              isPending && 'bg-info/10',
            )}
          >
            <span className="min-w-0 flex-1 flex flex-col justify-center gap-0.5">
              <span className="flex items-center gap-2">
                <span className="font-medium text-txt-primary truncate">{symbol}</span>
                {locked && <Lock size={14} className="text-txt-muted/60 shrink-0" />}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="font-mono tabular-nums text-txt-secondary text-xs">{fmtPrice(row.quote?.ltp ?? row.ltp)}</span>
                <ChangePercentBadge quote={row.quote} className="text-2xs" />
                <ActiveTradePill trade={activeTrade} />
              </span>
            </span>
            {/* Remove symbol — reuses the existing onDelete flow (requestDeleteRow → confirm modal).
                stopPropagation keeps the row-body tap (open order sheet) from firing. */}
            <button
              type="button"
              title="Remove symbol"
              aria-label={`Remove ${symbol} from watchlist`}
              onClick={(e) => { e.stopPropagation(); onDelete(row); }}
              className="mt-delete shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-bear border border-bear text-white hover:bg-bear/90 active:bg-bear/80 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}

      {activeRow && (
        <OrderEntrySheet
          strategy={strategy}
          row={activeRow}
          riskUnit={riskUnit}
          onBuy={onBuy}
          onSell={onSell}
          onEditCell={onEditCell}
          onDelete={onDelete}
          onRowRiskUnitChange={onRowRiskUnitChange}
          onClose={() => setActiveKey(null)}
        />
      )}
    </div>
  );
}
